import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    CheckCircle, XCircle, MinusCircle, ChevronDown, ChevronUp,
    TrendingUp, Clock, Target, Award, ArrowLeft, Filter
} from 'lucide-react'

// Mock test data with questions and answers
const mockTestHistory = [
    {
        id: 'mock-4',
        name: 'Weekly Mock Test 4',
        date: 'Oct 16, 2024',
        score: 620,
        maxScore: 720,
        duration: '2h 45m',
        totalQuestions: 180,
        attempted: 165,
        correct: 155,
        incorrect: 10,
        skipped: 15,
        subjectWise: {
            physics: { correct: 40, total: 45, score: 155 },
            chemistry: { correct: 42, total: 45, score: 163 },
            biology: { correct: 73, total: 90, score: 302 },
        },
        questions: [
            { id: 1, subject: 'Physics', chapter: 'Thermodynamics', question: 'The efficiency of a Carnot engine working between 500K and 300K is:', options: ['40%', '50%', '60%', '80%'], correctAnswer: 0, userAnswer: 0, explanation: 'Efficiency = 1 - T₂/T₁ = 1 - 300/500 = 0.4 = 40%' },
            { id: 2, subject: 'Physics', chapter: 'Thermodynamics', question: 'In an adiabatic process, which quantity remains constant?', options: ['Temperature', 'Pressure', 'Volume', 'Entropy'], correctAnswer: 3, userAnswer: 1, explanation: 'In an adiabatic process, there is no heat exchange with surroundings, so entropy remains constant (for reversible process).' },
            { id: 3, subject: 'Chemistry', chapter: 'Organic Chemistry', question: 'Which of the following is an example of nucleophilic substitution?', options: ['Friedel-Crafts alkylation', 'Wurtz reaction', 'SN1 reaction', 'Electrophilic addition'], correctAnswer: 2, userAnswer: 2, explanation: 'SN1 (Substitution Nucleophilic Unimolecular) is a nucleophilic substitution reaction.' },
            { id: 4, subject: 'Chemistry', chapter: 'Electrochemistry', question: 'The standard electrode potential of Zn is:', options: ['-0.76V', '+0.76V', '-1.66V', '+1.66V'], correctAnswer: 0, userAnswer: null, explanation: 'Standard reduction potential of Zn²⁺/Zn is -0.76V' },
            { id: 5, subject: 'Biology', chapter: 'Human Physiology', question: 'The pacemaker of the heart is:', options: ['AV node', 'SA node', 'Bundle of His', 'Purkinje fibers'], correctAnswer: 1, userAnswer: 1, explanation: 'The Sinoatrial (SA) node is known as the natural pacemaker of the heart as it initiates the cardiac impulse.' },
            { id: 6, subject: 'Biology', chapter: 'Genetics', question: 'Codominance is observed in:', options: ['ABO blood group', 'Skin color', 'Height', 'Eye color'], correctAnswer: 0, userAnswer: 0, explanation: 'In ABO blood group, alleles Iᴬ and Iᴮ are codominant, producing blood type AB.' },
        ]
    },
    {
        id: 'mock-3',
        name: 'Weekly Mock Test 3',
        date: 'Oct 9, 2024',
        score: 610,
        maxScore: 720,
        duration: '2h 50m',
        totalQuestions: 180,
        attempted: 160,
        correct: 150,
        incorrect: 10,
        skipped: 20,
        subjectWise: {
            physics: { correct: 38, total: 45, score: 147 },
            chemistry: { correct: 40, total: 45, score: 155 },
            biology: { correct: 72, total: 90, score: 308 },
        },
        questions: []
    },
]

interface TestHistoryViewProps {
    onBack?: () => void
    testId?: string
}

export function TestHistoryView({ testId }: TestHistoryViewProps) {
    const [selectedTest, setSelectedTest] = useState<typeof mockTestHistory[0] | null>(
        testId ? mockTestHistory.find(t => t.id === testId) || null : null
    )
    const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null)
    const [subjectFilter, setSubjectFilter] = useState<'all' | 'physics' | 'chemistry' | 'biology'>('all')
    const [statusFilter, setStatusFilter] = useState<'all' | 'correct' | 'incorrect' | 'skipped'>('all')

    const subjects = ['all', 'physics', 'chemistry', 'biology'] as const
    const statuses = ['all', 'correct', 'incorrect', 'skipped'] as const

    const filteredQuestions = selectedTest?.questions.filter(q => {
        const matchesSubject = subjectFilter === 'all' || q.subject.toLowerCase() === subjectFilter
        const isCorrect = q.userAnswer === q.correctAnswer
        const isSkipped = q.userAnswer === null
        const isIncorrect = q.userAnswer !== null && q.userAnswer !== q.correctAnswer

        if (statusFilter === 'correct' && !isCorrect) return false
        if (statusFilter === 'incorrect' && !isIncorrect) return false
        if (statusFilter === 'skipped' && !isSkipped) return false

        return matchesSubject
    }) || []

    if (selectedTest) {
        return (
            <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setSelectedTest(null)}
                        className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5 text-theme-secondary" />
                    </button>
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-theme-primary">{selectedTest.name}</h2>
                        <p className="text-sm text-theme-secondary">{selectedTest.date} • Duration: {selectedTest.duration}</p>
                    </div>
                </div>

                {/* Score Overview */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <GlassmorphicCard className="p-4 text-center">
                        <div className="w-10 h-10 rounded-xl bg-primary-blue/10 flex items-center justify-center mx-auto mb-2">
                            <Award className="h-5 w-5 text-primary-blue" />
                        </div>
                        <p className="text-2xl font-bold text-primary-blue">{selectedTest.score}</p>
                        <p className="text-xs text-theme-secondary">/{selectedTest.maxScore}</p>
                    </GlassmorphicCard>
                    <GlassmorphicCard className="p-4 text-center">
                        <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center mx-auto mb-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <p className="text-2xl font-bold text-green-600">{selectedTest.correct}</p>
                        <p className="text-xs text-theme-secondary">Correct</p>
                    </GlassmorphicCard>
                    <GlassmorphicCard className="p-4 text-center">
                        <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-2">
                            <XCircle className="h-5 w-5 text-red-500" />
                        </div>
                        <p className="text-2xl font-bold text-red-600">{selectedTest.incorrect}</p>
                        <p className="text-xs text-theme-secondary">Incorrect</p>
                    </GlassmorphicCard>
                    <GlassmorphicCard className="p-4 text-center">
                        <div className="w-10 h-10 rounded-xl bg-slate-500/10 flex items-center justify-center mx-auto mb-2">
                            <MinusCircle className="h-5 w-5 text-slate-500" />
                        </div>
                        <p className="text-2xl font-bold text-slate-600">{selectedTest.skipped}</p>
                        <p className="text-xs text-theme-secondary">Skipped</p>
                    </GlassmorphicCard>
                </div>

                {/* Subject-wise Performance */}
                <GlassmorphicCard className="p-4 md:p-6">
                    <h3 className="font-bold text-theme-primary mb-4 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-primary-blue" />
                        Subject-wise Performance
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                        {Object.entries(selectedTest.subjectWise).map(([subject, data]) => (
                            <div key={subject} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-semibold text-theme-primary capitalize">{subject}</span>
                                    <span className="text-sm font-bold text-primary-blue">{data.score}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-primary-blue to-accent-gold rounded-full"
                                            style={{ width: `${(data.correct / data.total) * 100}%` }}
                                        />
                                    </div>
                                    <span className="text-xs text-theme-secondary">{data.correct}/{data.total}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </GlassmorphicCard>

                {/* Question Filter */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Filter className="h-4 w-4 text-theme-secondary" />
                        <span className="text-sm text-theme-secondary">Subject:</span>
                        {subjects.map(s => (
                            <button
                                key={s}
                                onClick={() => setSubjectFilter(s)}
                                className={`px-3 py-1 text-xs font-medium rounded-full capitalize transition-colors ${subjectFilter === s
                                    ? 'bg-primary-blue text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:text-theme-primary'
                                    }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-theme-secondary">Status:</span>
                        {statuses.map(s => (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`px-3 py-1 text-xs font-medium rounded-full capitalize transition-colors ${statusFilter === s
                                    ? s === 'correct' ? 'bg-green-500 text-white' :
                                        s === 'incorrect' ? 'bg-red-500 text-white' :
                                            s === 'skipped' ? 'bg-slate-500 text-white' :
                                                'bg-primary-blue text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:text-theme-primary'
                                    }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Question Analysis */}
                <GlassmorphicCard className="p-4 md:p-6">
                    <h3 className="font-bold text-theme-primary mb-4">Question Analysis ({filteredQuestions.length} questions)</h3>
                    <div className="space-y-3">
                        {filteredQuestions.length === 0 ? (
                            <p className="text-center text-theme-secondary py-8">No questions match your filters</p>
                        ) : (
                            filteredQuestions.map((q, i) => {
                                const isCorrect = q.userAnswer === q.correctAnswer
                                const isSkipped = q.userAnswer === null

                                return (
                                    <motion.div
                                        key={q.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                        className={`border rounded-xl overflow-hidden ${isSkipped ? 'border-slate-300 dark:border-slate-700' :
                                            isCorrect ? 'border-green-200 dark:border-green-800' :
                                                'border-red-200 dark:border-red-800'
                                            }`}
                                    >
                                        <button
                                            onClick={() => setExpandedQuestion(expandedQuestion === q.id ? null : q.id)}
                                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 text-left">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${isSkipped ? 'bg-slate-400' : isCorrect ? 'bg-green-500' : 'bg-red-500'
                                                    }`}>
                                                    {q.id}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-theme-primary text-sm line-clamp-1">{q.question}</p>
                                                    <p className="text-xs text-theme-secondary">{q.subject} • {q.chapter}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${isSkipped ? 'bg-slate-100 text-slate-600' :
                                                    isCorrect ? 'bg-green-100 text-green-600' :
                                                        'bg-red-100 text-red-600'
                                                    }`}>
                                                    {isSkipped ? 'Skipped' : isCorrect ? 'Correct' : 'Incorrect'}
                                                </span>
                                                {expandedQuestion === q.id ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                                            </div>
                                        </button>

                                        <AnimatePresence>
                                            {expandedQuestion === q.id && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="border-t border-slate-100 dark:border-slate-800"
                                                >
                                                    <div className="p-4 bg-slate-50 dark:bg-slate-900/50 space-y-4">
                                                        <p className="text-theme-primary font-medium">{q.question}</p>

                                                        <div className="grid sm:grid-cols-2 gap-2">
                                                            {q.options.map((opt, idx) => (
                                                                <div key={idx} className={`p-3 rounded-lg border-2 flex items-center gap-2 ${idx === q.correctAnswer
                                                                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                                                    : idx === q.userAnswer
                                                                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                                                        : 'border-slate-200 dark:border-slate-700'
                                                                    }`}>
                                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === q.correctAnswer
                                                                        ? 'bg-green-500 text-white'
                                                                        : idx === q.userAnswer
                                                                            ? 'bg-red-500 text-white'
                                                                            : 'bg-slate-200 dark:bg-slate-700 text-slate-600'
                                                                        }`}>
                                                                        {String.fromCharCode(65 + idx)}
                                                                    </div>
                                                                    <span className={`text-sm ${idx === q.correctAnswer ? 'text-green-700 dark:text-green-400 font-semibold' : 'text-theme-secondary'
                                                                        }`}>{opt}</span>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                                                            <p className="font-semibold text-primary-blue text-sm mb-1">Explanation</p>
                                                            <p className="text-sm text-theme-secondary">{q.explanation}</p>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                )
                            })
                        )}
                    </div>
                </GlassmorphicCard>
            </div>
        )
    }

    // Test History List
    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Test History</h2>
                <span className="text-sm text-theme-secondary">{mockTestHistory.length} tests completed</span>
            </div>

            <div className="space-y-3">
                {mockTestHistory.map((test, i) => (
                    <motion.div
                        key={test.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                    >
                        <GlassmorphicCard
                            className="p-4 md:p-5 hover:shadow-lg transition-shadow cursor-pointer"
                            onClick={() => setSelectedTest(test)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center">
                                        <span className="text-white font-bold text-lg">#{test.id.split('-')[1]}</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-theme-primary">{test.name}</h3>
                                        <div className="flex items-center gap-3 text-sm text-theme-secondary">
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3.5 w-3.5" />
                                                {test.date}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Target className="h-3.5 w-3.5" />
                                                {test.attempted}/{test.totalQuestions}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-primary-blue">{test.score}</p>
                                    <p className="text-xs text-theme-secondary">/{test.maxScore}</p>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="mt-4 flex items-center gap-2">
                                <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex">
                                    <div
                                        className="h-full bg-green-500"
                                        style={{ width: `${(test.correct / test.totalQuestions) * 100}%` }}
                                    />
                                    <div
                                        className="h-full bg-red-500"
                                        style={{ width: `${(test.incorrect / test.totalQuestions) * 100}%` }}
                                    />
                                </div>
                                <span className="text-xs text-theme-secondary whitespace-nowrap">
                                    {test.correct}✓ {test.incorrect}✗ {test.skipped}−
                                </span>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                ))}
            </div>
        </div>
    )
}
